import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Clock, Save, X, MapPin, Navigation, ExternalLink } from 'lucide-react';
import apiService from '../services/api';
import { buildNavigationLinks } from '../utils/navigation';

const safe = (x) => (Array.isArray(x) ? x : []);
const buildDefaultCreneau = () => ({
  heureDebut: '09:00',
  heureFin: '18:00',
  actif: true,
  libelleLieu: '',
  adresseLigne1: '',
  adresseLigne2: '',
  codePostal: '',
  ville: '',
  latitude: '',
  longitude: ''
});

const withCreneauDefaults = (creneau = {}) => ({
  ...buildDefaultCreneau(),
  ...creneau,
  actif: creneau?.actif !== undefined ? Boolean(creneau.actif) : true
});

const normalizeLocalHoraires = (horairesSource) => safe(horairesSource).map(h => ({
  ...h,
  creneaux: safe(h?.creneaux).map(withCreneauDefaults)
}));

const HorairesManager = ({ horaires = [], onSave = () => {}, loading = false, isItinerant = false, defaultEditing = false, startInEditMode = false }) => {
  const [localHoraires, setLocalHoraires] = useState(normalizeLocalHoraires(horaires));
  const [editing, setEditing] = useState(defaultEditing || startInEditMode);
  const [geocodeLoadingKey, setGeocodeLoadingKey] = useState(null);

  useEffect(() => {
    if (horaires && Array.isArray(horaires) && horaires.length > 0) {
      setLocalHoraires(normalizeLocalHoraires(horaires));
    } else {
      const joursSemaine = [
        'LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI', 'DIMANCHE'
      ];
      setLocalHoraires(joursSemaine.map(jour => ({
        jour,
        creneaux: [withCreneauDefaults()]
      })));
    }
  }, [horaires]);

  useEffect(() => {
    if (horaires && Array.isArray(horaires) && horaires.length > 0) {
      setLocalHoraires(normalizeLocalHoraires(horaires));
    }
  }, [horaires]);

  const handleCreneauChange = (jourIndex, creneauIndex, field, value) => {
    const newHoraires = [...localHoraires];
    newHoraires[jourIndex].creneaux[creneauIndex][field] = value;
    
    // Si on décoche "actif" (Ouvert), vider les horaires
    if (field === 'actif' && !value) {
      newHoraires[jourIndex].creneaux[creneauIndex].heureDebut = '';
      newHoraires[jourIndex].creneaux[creneauIndex].heureFin = '';
    }
    // Si on coche "actif" (Ouvert) et que les horaires sont vides, les remplir avec des valeurs par défaut
    else if (field === 'actif' && value && 
             (!newHoraires[jourIndex].creneaux[creneauIndex].heureDebut || 
              !newHoraires[jourIndex].creneaux[creneauIndex].heureFin)) {
      newHoraires[jourIndex].creneaux[creneauIndex].heureDebut = '09:00';
      newHoraires[jourIndex].creneaux[creneauIndex].heureFin = '18:00';
    }
    
    setLocalHoraires(newHoraires);
    
    // Si on est en mode defaultEditing (formulaire de création), sauvegarder automatiquement
    if (defaultEditing) {
      const horairesNettoyes = newHoraires.map(horaire => ({
        ...horaire,
        creneaux: horaire.creneaux.map(creneau => ({
          ...creneau,
          heureDebut: creneau.heureDebut || '',
          heureFin: creneau.heureFin || '',
          actif: Boolean(creneau.actif),
          ordre: typeof creneau.ordre === 'number' ? creneau.ordre : 0,
          libelleLieu: (creneau.libelleLieu || '').trim(),
          adresseLigne1: (creneau.adresseLigne1 || '').trim(),
          adresseLigne2: (creneau.adresseLigne2 || '').trim(),
          codePostal: (creneau.codePostal || '').trim(),
          ville: (creneau.ville || '').trim(),
          latitude: (creneau.latitude || '').trim(),
          longitude: (creneau.longitude || '').trim()
        }))
      }));
      onSave(horairesNettoyes);
    }
  };

  const handleGeocode = async (jourIndex, creneauIndex) => {
    const geocodeKey = `${jourIndex}-${creneauIndex}`;
    const cible = localHoraires[jourIndex].creneaux[creneauIndex];

    if (!cible.adresseLigne1 || !cible.codePostal || !cible.ville) {
      window.alert('Merci de renseigner l’adresse, le code postal et la ville avant de récupérer les coordonnées GPS.');
      return;
    }

    try {
      setGeocodeLoadingKey(geocodeKey);
      const response = await apiService.geocodeAdresse({
        adresseLigne1: cible.adresseLigne1,
        adresseLigne2: cible.adresseLigne2,
        codePostal: cible.codePostal,
        ville: cible.ville,
        pays: 'France',
      });

      const updatedHoraires = [...localHoraires];
      updatedHoraires[jourIndex].creneaux[creneauIndex] = {
        ...updatedHoraires[jourIndex].creneaux[creneauIndex],
        latitude: response?.latitude !== undefined && response?.latitude !== null
          ? String(response.latitude)
          : '',
        longitude: response?.longitude !== undefined && response?.longitude !== null
          ? String(response.longitude)
          : '',
      };

      setLocalHoraires(updatedHoraires);
      window.alert('Coordonnées GPS ajoutées automatiquement 👍');
    } catch (error) {
      console.error('Erreur géocodage:', error);
      const message =
        error?.error?.message ||
        error?.message ||
        'Impossible de récupérer des coordonnées pour cette adresse.';
      window.alert(message);
    } finally {
      setGeocodeLoadingKey(null);
    }
  };

  const addCreneau = (jourIndex) => {
    const newHoraires = [...localHoraires];
    newHoraires[jourIndex].creneaux.push(withCreneauDefaults());
    setLocalHoraires(newHoraires);
    
    // Si on est en mode defaultEditing (formulaire de création), sauvegarder automatiquement
    if (defaultEditing) {
      const horairesNettoyes = newHoraires.map(horaire => ({
        ...horaire,
        creneaux: horaire.creneaux.map(creneau => ({
          ...creneau,
          heureDebut: creneau.heureDebut || '',
          heureFin: creneau.heureFin || '',
          actif: Boolean(creneau.actif),
          ordre: typeof creneau.ordre === 'number' ? creneau.ordre : 0,
          libelleLieu: (creneau.libelleLieu || '').trim(),
          adresseLigne1: (creneau.adresseLigne1 || '').trim(),
          adresseLigne2: (creneau.adresseLigne2 || '').trim(),
          codePostal: (creneau.codePostal || '').trim(),
          ville: (creneau.ville || '').trim(),
          latitude: (creneau.latitude || '').trim(),
          longitude: (creneau.longitude || '').trim()
        }))
      }));
      onSave(horairesNettoyes);
    }
  };

  const removeCreneau = (jourIndex, creneauIndex) => {
    const newHoraires = [...localHoraires];
    if (newHoraires[jourIndex].creneaux.length > 1) {
      newHoraires[jourIndex].creneaux.splice(creneauIndex, 1);
      setLocalHoraires(newHoraires);
      
      // Si on est en mode defaultEditing (formulaire de création), sauvegarder automatiquement
      if (defaultEditing) {
        const horairesNettoyes = newHoraires.map(horaire => ({
          ...horaire,
          creneaux: horaire.creneaux.map(creneau => ({
            ...creneau,
            heureDebut: creneau.heureDebut || '',
            heureFin: creneau.heureFin || '',
            actif: Boolean(creneau.actif),
            ordre: typeof creneau.ordre === 'number' ? creneau.ordre : 0,
            libelleLieu: (creneau.libelleLieu || '').trim(),
            adresseLigne1: (creneau.adresseLigne1 || '').trim(),
            adresseLigne2: (creneau.adresseLigne2 || '').trim(),
            codePostal: (creneau.codePostal || '').trim(),
            ville: (creneau.ville || '').trim(),
            latitude: (creneau.latitude || '').trim(),
            longitude: (creneau.longitude || '').trim()
          }))
        }));
        onSave(horairesNettoyes);
      }
    }
  };

  const getJoursDisponibles = () => {
    const tousLesJours = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI', 'DIMANCHE'];
    const joursExistants = localHoraires.map(h => h.jour);
    return tousLesJours.filter(jour => !joursExistants.includes(jour));
  };

  const addJour = (jour) => {
    const newHoraires = [...localHoraires, {
      jour,
      creneaux: [withCreneauDefaults()]
    }];
    // Trier par ordre des jours de la semaine
    const ordreJours = ['LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI', 'DIMANCHE'];
    newHoraires.sort((a, b) => {
      const indexA = ordreJours.indexOf(a.jour);
      const indexB = ordreJours.indexOf(b.jour);
      return indexA - indexB;
    });
    setLocalHoraires(newHoraires);
    
    // Si on est en mode defaultEditing (formulaire de création), sauvegarder automatiquement
    if (defaultEditing) {
      const horairesNettoyes = newHoraires.map(horaire => ({
        ...horaire,
        creneaux: horaire.creneaux.map(creneau => ({
          ...creneau,
          heureDebut: creneau.heureDebut || '',
          heureFin: creneau.heureFin || '',
          actif: Boolean(creneau.actif),
          ordre: typeof creneau.ordre === 'number' ? creneau.ordre : 0,
          libelleLieu: (creneau.libelleLieu || '').trim(),
          adresseLigne1: (creneau.adresseLigne1 || '').trim(),
          adresseLigne2: (creneau.adresseLigne2 || '').trim(),
          codePostal: (creneau.codePostal || '').trim(),
          ville: (creneau.ville || '').trim(),
          latitude: (creneau.latitude || '').trim(),
          longitude: (creneau.longitude || '').trim()
        }))
      }));
      onSave(horairesNettoyes);
    }
  };

  const handleSave = () => {
    // Nettoyer les données avant de les envoyer
    const horairesNettoyes = localHoraires.map(horaire => ({
      ...horaire,
      creneaux: horaire.creneaux.map(creneau => ({
        ...creneau,
        // S'assurer que les horaires vides sont des chaînes vides, pas undefined
        heureDebut: creneau.heureDebut || '',
        heureFin: creneau.heureFin || '',
        // S'assurer que actif est un booléen
        actif: Boolean(creneau.actif),
        ordre: typeof creneau.ordre === 'number' ? creneau.ordre : 0,
        libelleLieu: (creneau.libelleLieu || '').trim(),
        adresseLigne1: (creneau.adresseLigne1 || '').trim(),
        adresseLigne2: (creneau.adresseLigne2 || '').trim(),
        codePostal: (creneau.codePostal || '').trim(),
        ville: (creneau.ville || '').trim(),
        latitude: (creneau.latitude || '').trim(),
        longitude: (creneau.longitude || '').trim()
      }))
    }));
    
    onSave(horairesNettoyes);
    setEditing(false);
  };

  const handleCancel = () => {
    if (horaires && Array.isArray(horaires)) {
      setLocalHoraires(normalizeLocalHoraires(horaires));
    }
    setEditing(false);
  };

  const getJourLabel = (jour) => {
    const labels = {
      'LUNDI': 'Lundi',
      'MARDI': 'Mardi',
      'MERCREDI': 'Mercredi',
      'JEUDI': 'Jeudi',
      'VENDREDI': 'Vendredi',
      'SAMEDI': 'Samedi',
      'DIMANCHE': 'Dimanche'
    };
    return labels[jour] || jour;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          Horaires d'ouverture
          {isItinerant && (
            <span className="ml-3 inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-medium text-blue-700">
              Magasin itinérant
            </span>
          )}
        </h3>
        {!editing ? (
          <button
            type="button"
            onClick={() => {
              // S'assurer qu'il y a des horaires à éditer
              if (localHoraires.length === 0) {
                const joursSemaine = [
                  'LUNDI', 'MARDI', 'MERCREDI', 'JEUDI', 'VENDREDI', 'SAMEDI', 'DIMANCHE'
                ];
                setLocalHoraires(joursSemaine.map(jour => ({
                  jour,
                  creneaux: [withCreneauDefaults()]
                })));
              }
              setEditing(true);
            }}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Modifier</span>
          </button>
        ) : (
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 flex items-center space-x-2"
            >
              <X className="h-4 w-4" />
              <span>Annuler</span>
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center space-x-2"
            >
              <Save className="h-4 w-4" />
              <span>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</span>
            </button>
          </div>
        )}
      </div>

      {localHoraires.length === 0 && !editing ? (
        <div className="text-center py-8 text-gray-500">
          <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>Aucun horaire configuré</p>
          <p className="text-sm">Cliquez sur "Modifier" pour configurer les horaires d'ouverture</p>
        </div>
      ) : (
        <div className="space-y-4">
          {editing && getJoursDisponibles().length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-blue-800">Ajouter un jour</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Sélectionnez un jour pour l'ajouter à vos horaires
                  </p>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      addJour(e.target.value);
                      e.target.value = ''; // Réinitialiser le select
                    }
                  }}
                  className="px-4 py-2 text-sm border border-blue-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  defaultValue=""
                >
                  <option value="">Choisir un jour...</option>
                  {getJoursDisponibles().map(jour => (
                    <option key={jour} value={jour}>
                      {getJourLabel(jour)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {safe(localHoraires).map((horaire, jourIndex) => (
          <div key={horaire.jour} className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-900">
                {getJourLabel(horaire.jour)}
              </h4>
              {editing && (
                <button
                  type="button"
                  onClick={() => addCreneau(jourIndex)}
                  className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                >
                  <Plus className="h-4 w-4" />
                  <span className="text-sm">Ajouter un créneau</span>
                </button>
              )}
            </div>

            <div className="space-y-3">
              {safe(horaire?.creneaux).map((creneau, creneauIndex) => {
                const hasLocationDetails = Boolean(
                  (creneau.libelleLieu && creneau.libelleLieu.trim() !== '') ||
                  (creneau.adresseLigne1 && creneau.adresseLigne1.trim() !== '') ||
                  (creneau.codePostal && creneau.codePostal.trim() !== '') ||
                  (creneau.ville && creneau.ville.trim() !== '') ||
                  (creneau.latitude && creneau.latitude.trim() !== '') ||
                  (creneau.longitude && creneau.longitude.trim() !== '')
                );
                const geocodeKey = `${jourIndex}-${creneauIndex}`;
                const navigationLinks = buildNavigationLinks(creneau);

                return (
                  <div key={creneauIndex} className="rounded-lg border border-gray-100 p-3">
                    {editing ? (
                      <div className="space-y-3">
                        <div className="flex flex-wrap items-center gap-3">
                          <input
                            type="time"
                            value={creneau.heureDebut}
                            onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'heureDebut', e.target.value)}
                            disabled={!creneau.actif}
                            className={`w-full sm:w-auto flex-1 min-w-[120px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !creneau.actif 
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                                : 'border-gray-300 bg-white'
                            }`}
                          />
                          <span className={`${!creneau.actif ? 'text-gray-400' : 'text-gray-500'}`}>-</span>
                          <input
                            type="time"
                            value={creneau.heureFin}
                            onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'heureFin', e.target.value)}
                            disabled={!creneau.actif}
                            className={`w-full sm:w-auto flex-1 min-w-[120px] px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              !creneau.actif 
                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed' 
                                : 'border-gray-300 bg-white'
                            }`}
                          />
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={creneau.actif}
                              onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'actif', e.target.checked)}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-600">Ouvert</span>
                          </label>
                          {safe(horaire?.creneaux).length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCreneau(jourIndex, creneauIndex)}
                              className="text-red-600 hover:text-red-700"
                              title="Supprimer ce créneau"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>

                        {isItinerant && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="md:col-span-2">
                              <input
                                type="text"
                                value={creneau.libelleLieu}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'libelleLieu', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Nom du lieu (ex: Place du Marché)"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <input
                                type="text"
                                value={creneau.adresseLigne1}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'adresseLigne1', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Adresse (ligne 1) *"
                              />
                            </div>
                            <div>
                              <input
                                type="text"
                                value={creneau.adresseLigne2}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'adresseLigne2', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Complément (optionnel)"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={creneau.codePostal}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'codePostal', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Code postal *"
                              />
                              <input
                                type="text"
                                value={creneau.ville}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'ville', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Ville *"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="text"
                                value={creneau.latitude}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'latitude', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Latitude (optionnel)"
                              />
                              <input
                                type="text"
                                value={creneau.longitude}
                                onChange={(e) => handleCreneauChange(jourIndex, creneauIndex, 'longitude', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Longitude (optionnel)"
                              />
                            </div>
                            <div className="md:col-span-2 flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleGeocode(jourIndex, creneauIndex)}
                                disabled={geocodeLoadingKey === geocodeKey}
                                className={`inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-white transition ${
                                  geocodeLoadingKey === geocodeKey
                                    ? 'bg-blue-400 cursor-wait'
                                    : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                              >
                                <Navigation className="h-4 w-4" />
                                {geocodeLoadingKey === geocodeKey ? 'Recherche…' : 'Récupérer les coordonnées'}
                              </button>
                              {creneau.latitude && creneau.longitude && (
                                <span className="text-xs text-gray-500">
                                  Lat {creneau.latitude} • Lon {creneau.longitude}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        {creneau.actif ? (
                          <span className="text-sm text-gray-600">
                            {creneau.heureDebut} - {creneau.heureFin}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400 italic">
                            Fermé
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          creneau.actif 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {creneau.actif ? 'Ouvert' : 'Fermé'}
                        </span>
                      </div>
                    )}

                    {!editing && isItinerant && hasLocationDetails && (
                      <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
                        <div className="flex items-start gap-2">
                          <MapPin className="mt-0.5 h-4 w-4 text-gray-500" />
                          <div className="space-y-1">
                            {creneau.libelleLieu && (
                              <p className="font-medium text-gray-900">{creneau.libelleLieu}</p>
                            )}
                            {(creneau.adresseLigne1 || creneau.adresseLigne2) && (
                              <p>
                                {[creneau.adresseLigne1, creneau.adresseLigne2].filter(Boolean).join(', ')}
                              </p>
                            )}
                            {(creneau.codePostal || creneau.ville) && (
                              <p>
                                {[creneau.codePostal, creneau.ville].filter(Boolean).join(' ')}
                              </p>
                            )}
                            {(creneau.latitude || creneau.longitude) && (
                              <p className="text-xs text-gray-500">
                                {creneau.latitude ? `Lat: ${creneau.latitude}` : ''}
                                {creneau.latitude && creneau.longitude ? ' • ' : ''}
                                {creneau.longitude ? `Lon: ${creneau.longitude}` : ''}
                              </p>
                            )}
                          </div>
                        </div>
                        {navigationLinks.length > 0 && (
                          <div className="mt-3 flex flex-wrap items-center gap-2">
                            {navigationLinks.map((link) => (
                              <a
                                key={link.id}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 rounded-md bg-white px-3 py-1 text-xs font-medium text-blue-700 shadow-sm ring-1 ring-inset ring-blue-100 hover:bg-blue-50"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                {link.label}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <Clock className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-800">Double shift</h4>
              <p className="text-sm text-blue-700 mt-1">
                Vous pouvez ajouter plusieurs créneaux par jour pour gérer les pauses déjeuner 
                ou les ouvertures en deux temps (ex: 9h-12h et 14h-18h).
              </p>
            {isItinerant && (
              <p className="text-sm text-blue-700 mt-2">
                Pour un commerce itinérant, renseignez l’adresse précise de chaque créneau (midi et soir)
                afin que les clients sachent exactement où vous retrouver.
              </p>
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HorairesManager;
